import {ToolExecuteParams, ToolResult} from 'common/src/modules/shared/copilot/copilot.types';
import tool from 'common/src/modules/shared/copilot/utils/creators/tool';
import toolResult from 'common/src/modules/shared/copilot/utils/creators/toolResult';
import toolError from 'common/src/modules/shared/copilot/utils/toolError';
import {z} from 'zod';
import {NodeOutputData, NodeType, OutputType} from 'common/src/modules/nodes/nodes.types';
import {V2CardAgentToolName} from '../../promptToCardAgentV2.enums';
import {ProductCardScratchpadState} from '../../scratchpad/productCardScratchpad.types';
import validateAgentScratchpad from '../../utils/general/validateAgentScratchpad';
import {ProductSelectionStrategyType} from 'common/src/modules/productSelectionStrategies/productSelectionStrategies.types';
import productFeedApi from 'common/src/modules/productFeed/productFeed.api';
import {NO_NODE_ID} from 'common/src/components/CLCardsMap/CLCardsMap.config';
import describeIdFormat from 'common/src/modules/campaigns/copilot/skills/campaignSetupSkill/idHelpers/utils/describeIdFormat';
import {CLStore} from 'common/src/redux/store';
import productSelectionStrategiesApi from 'common/src/modules/productSelectionStrategies/productSelectionStrategies.api';
import {idHelper} from 'common/src/modules/campaigns/copilot/skills/campaignSetupSkill/idHelpers/idHelpers.config';
import {IdPrefix} from 'common/src/modules/campaigns/copilot/skills/campaignSetupSkill/idHelpers/idHelpers.enums';
import formatParseError from 'common/src/modules/campaigns/copilot/skills/campaignSetupSkill/idHelpers/utils/formatParseError';

const setStrategyToolSchema = z.object({
  strategyId: z.string().describe(describeIdFormat(IdPrefix.ProductStrategy)),
});

export default function setStrategyTool(store: CLStore) {
  async function execute({
    input: {strategyId},
    toolUtils: {agentScratchpad},
  }: ToolExecuteParams<
    typeof setStrategyToolSchema,
    ProductCardScratchpadState
  >): Promise<ToolResult> {
    try {
      const validatedScratchpad = validateAgentScratchpad<ProductCardScratchpadState>(
        agentScratchpad,
        NodeType.PRODUCTS,
      );

      const parseResult = idHelper.parseProductStrategyId(strategyId);

      if (parseResult.ok === false) {
        return toolError(formatParseError(parseResult.error));
      }

      const parsedStrategyId = parseResult.numericId;

      const campaign = store.getState().campaignPage.campaignTree.campaign;

      if (!campaign) {
        return toolError('Campaign not found');
      }

      const availableStrategies = await productSelectionStrategiesApi.getProductStrategies(
        campaign.id,
      );

      if (!availableStrategies.some((s) => s.strategy === parsedStrategyId)) {
        return toolError(
          `Strategy with ID ${strategyId} is not available for this card. Available strategies: ${availableStrategies.map((s) => `${idHelper.toProductStrategyId(s.strategy)}: ${s.uiName}`).join(', ')}`,
        );
      }

      //We need to remove related items
      validatedScratchpad.card.topItemLogicId = 0;
      validatedScratchpad.card.categoryTopItemsLogicId = 0;
      validatedScratchpad.card.retargetingProductSetId = 0;
      validatedScratchpad.card.productIds = [];
      validatedScratchpad.card.feedId = 0;

      const availableFeeds = await productFeedApi.getStrategyOptionalFeeds(parsedStrategyId);
      validatedScratchpad.card.availableFeeds = availableFeeds;
      validatedScratchpad.card.productsSelectionStrategy = parsedStrategyId;

      // Rebuild outputs with correct ordering: Success, Partial Success (if custom matching), Fallback
      const fallbackOutput = validatedScratchpad.card.outputs.find(
        (o) => o.type === OutputType.Fallback,
      );
      const successOutput = validatedScratchpad.card.outputs.find(
        (o) => o.type === OutputType.Regular,
      );

      const newOutputs: NodeOutputData[] = [];

      // Add Success output (always present)
      if (successOutput) {
        newOutputs.push(successOutput);
      } else {
        newOutputs.push({
          id: 1,
          name: 'Success',
          type: OutputType.Regular,
          nodeId: NO_NODE_ID,
        });
      }

      // Add Partial Success output only for Product Matching strategy
      if (parsedStrategyId === ProductSelectionStrategyType.ProductMatching) {
        newOutputs.push({
          id: 2,
          name: 'Partial Success',
          type: OutputType.PartialSuccess,
          nodeId: NO_NODE_ID,
        });
      }

      // Add Fallback output (always present)
      if (fallbackOutput) {
        newOutputs.push({...fallbackOutput, id: newOutputs.length + 1});
      } else {
        newOutputs.push({
          id: newOutputs.length + 1,
          name: 'Fallback',
          type: OutputType.Fallback,
          nodeId: NO_NODE_ID,
        });
      }

      validatedScratchpad.card.outputs = newOutputs;

      return toolResult(`Strategy set to: ${idHelper.toProductStrategyId(parsedStrategyId)}`);
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Unknown error';

      return toolError(`Error setting strategy: ${message}`);
    }
  }

  return tool({
    name: V2CardAgentToolName.SetStrategy,
    displayName: 'Set strategy',
    description:
      'Set the product strategy for the card. The strategy must be one from the available strategies.',
    execute,
    inputSchema: setStrategyToolSchema,
  });
}
